import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, finalize, tap, timeout } from 'rxjs';
import { environment } from '../../environments';
import { AuthTokenService } from './auth-token.service';

const LOGIN_TIMEOUT_MS = 15000;

interface LoginResponse {
  token: string;
  user: {
    id: string | number;
    name: string;
    email: string;
    role?: 'dealer' | 'nsc';
    roleId?: string | number;
    roleID?: string | number;
    roleName?: string;
    dealerId: string | number;
    dealerName: string;
    nscId: string | null;
    nscName: string | null;
    userId?: string | number;
    language?: string | null;
  };
}

export interface CurrentLoginContext {
  nscId: string | null;
  nscName: string | null;
  role: 'dealer' | 'nsc';
  roleId: string | null;
  roleName: string | null;
  dealerId: string | null;
  dealerName: string | null;
  userId: string | null;
  userName: string | null;
  siteCode: string | null;
  language: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<LoginResponse | null>(null);

  private currentContextSubject = new BehaviorSubject<CurrentLoginContext | null>(null);
  public currentContext$ = this.currentContextSubject.asObservable();

  private apiBaseUrl = environment.apiUrl; 

  constructor(
    private http: HttpClient,
    private readonly authTokenService: AuthTokenService
  ) {
    this.loadUserFromStorage();
    this.authTokenService.accessToken$.subscribe((token) => {
      if (token && !this.currentContextSubject.value) {
        this.getCurrentUser().subscribe({
          error: () => console.warn('[AuthService] external token rehydrate failed')
        });
      }
    });
  }

  /**
   * Login user
   */
  login(
    siteCode: string,
    dealerNscId: string,
    password: string,
    role: 'dealer' | 'nsc',
    rememberMe: boolean,
    language: 'en' | 'fr' = 'fr'
  ): Observable<LoginResponse> {
    const credentials = { siteCode, dealerNscId, password, role };
    return this.http
      .post<LoginResponse>(`${this.apiBaseUrl}/Auth/login`, credentials)
      .pipe(
        timeout({ first: LOGIN_TIMEOUT_MS }),
        tap((response) => {
          
          // Store token
          localStorage.setItem('auth_token', response.token);

          // Remove any previously persisted user state before writing the current session role.
          // This prevents stale NSC/dealer data from being restored after a role switch or a failed/partial login.
          this.clearStoredUserState();

          // The backend authenticates against the selected role (403 otherwise), so it is the authoritative source here.
          const resolvedRole = role;
          console.log('[AuthService] login -> raw user', response.user, 'resolved role', resolvedRole);

          // Persist user data so a full page reload can restore the context.
          // localStorage persists across browser restarts (remember me); sessionStorage lasts only for the tab session.
          const userDataJson = JSON.stringify({ ...response.user, role: resolvedRole });
          if (rememberMe) {
            localStorage.setItem('user_data', userDataJson);
            sessionStorage.removeItem('user_data');
          } else {
            sessionStorage.setItem('user_data', userDataJson);
            localStorage.removeItem('user_data');
          }

          const context: CurrentLoginContext = {
            role: resolvedRole,
            roleId: this.toStringOrNull(response.user.roleId ?? response.user.roleID),
            roleName: response.user.roleName ?? null,
            dealerId: resolvedRole === 'dealer' ? this.toStringOrNull(response.user.dealerId) : null,
            dealerName: resolvedRole === 'dealer' ? response.user.dealerName || response.user.name || null : null,
            nscId: resolvedRole === 'nsc' ? response.user.nscId || null : null,
            nscName: resolvedRole === 'nsc' ? response.user.nscName || null : null,
            userId: this.toStringOrNull(response.user.userId ?? response.user.id),
            userName: response.user.name || null,
            siteCode: siteCode || null,
            language: language || response.user.language || null   // user-selected language (flag click) takes priority
          };
          console.log('[AuthService] login -> context set', context);
                   this.currentContextSubject.next(context);
          this.currentUserSubject.next(response);
        })
      );
  }

  /**
   * Logout user
   */
  logout(): Observable<void> {
    this.authTokenService.beginLogout();
    return this.http.post<void>(`${this.apiBaseUrl}/Auth/logout`, {}).pipe(
      // Clear locally even when the backend call fails, so logout can't leave a usable session.
      finalize(() => this.clearSession())
    );
  }

  private clearSession(): void {
    console.log('[AuthService] logout -> context cleared');
    localStorage.removeItem('auth_token');
    this.clearStoredUserState();
    this.authTokenService.clearSession();

    this.currentContextSubject.next(null);
    this.currentUserSubject.next(null);
  }

  private clearStoredUserState(): void {
    localStorage.removeItem('user_data');
    sessionStorage.removeItem('user_data');
    console.log('clearStoredUserState -> localStorage and sessionStorage cleared');
  }

  /**
   * Request password reset
   */
  requestPasswordReset(email: string): Observable<any> {
    return this.http.post(`${this.apiBaseUrl}/Auth/forgot-password`, { email });
  }

  /**
   * Reset password with token
   */
  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiBaseUrl}/Auth/reset-password`, { token, newPassword });
  }

  /**
   * Get current user synchronously
   */
  getCurrentUserSync(): LoginResponse | null {
    return this.currentUserSubject.value;
  }

  getCurrentContextSync(): CurrentLoginContext | null {
    console.log('[AuthService] getCurrentContextSync ->', this.currentContextSubject.value);
    return this.currentContextSubject.value;
  }

  /**
   * Load the authenticated user from the backend and publish the result to the application.
   */
  getCurrentUser(): Observable<LoginResponse> {
    console.log('[REM flow] AuthService.getCurrentUser');
    return this.http.get<LoginResponse>(`${this.apiBaseUrl}/Auth`).pipe(
      tap((response) => {
        const previous = this.currentContextSubject.value;
        const role = previous?.role ?? this.resolveRole(response.user);
        const context: CurrentLoginContext = {
          // Prefer the role resolved by the backend from the validated token over any cached role.
          role: this.resolveRole(response.user, previous?.role ?? 'dealer'),
          roleId: this.toStringOrNull(response.user.roleId ?? response.user.roleID) ?? previous?.roleId ?? null,
          roleName: response.user.roleName ?? previous?.roleName ?? null,
          dealerId: role === 'dealer' ? this.toStringOrNull(response.user.dealerId) ?? previous?.dealerId ?? null : null,
          dealerName: role === 'dealer' ? response.user.dealerName || response.user.name || previous?.dealerName || null : null,
          nscId: role === 'nsc' ? response.user.nscId || previous?.nscId || null : null,
          nscName: role === 'nsc' ? response.user.nscName || previous?.nscName || null : null,
          userId: this.toStringOrNull(response.user.userId ?? response.user.id) ?? previous?.userId ?? null,
          userName: response.user.name || previous?.userName || null,
          siteCode: previous?.siteCode || null,
          language: response.user.language || previous?.language || null
        };

        console.log('[AuthService] getCurrentUser -> raw response', response);
        console.log('[AuthService] getCurrentUser -> context overwrite', context);
      
        this.currentContextSubject.next(context);
        this.currentUserSubject.next(response);
        console.log('[REM flow] AuthService.getCurrentUser: context loaded', {
          role: context.role,
          dealerId: context.dealerId,
          language: context.language
        });
        //localStorage.setItem('user_data', JSON.stringify(response.user));
      })
    );
  }

  /**
   * Load user from storage
   */
  private loadUserFromStorage(): void {
    const stored = localStorage.getItem('user_data') ?? sessionStorage.getItem('user_data');
    console.log('1. [AuthService] loadUserFromStorage -> stored user_data', stored);
    if (!stored) {
      // Token-only session (e.g. created before user_data was persisted): rehydrate from backend.
      if (this.getToken()) {
        console.log('2. [AuthService] loadUserFromStorage -> no user_data but token present, rehydrating');
        this.getCurrentUser().subscribe({
          error: () => console.warn('[AuthService] rehydrate failed; token may be invalid')
        });
      }
      return;
    }
    console.log('3. check getTocken()', this.getToken());
    if (!this.getToken()) {
      this.clearStoredUserState();
      return;
    }
    console.log('4. After getTocken()', this.getToken());
    {
      try {
        const parsedUser = JSON.parse(stored);
        const currentUser = {
          token: this.getToken() || '',
          user: parsedUser
        };
        const role = this.resolveRole(parsedUser);

        const context: CurrentLoginContext = {
          role,
          roleId: this.toStringOrNull(parsedUser.roleId ?? parsedUser.roleID),
          roleName: parsedUser.roleName ?? null,
          dealerId: role === 'dealer' ? this.toStringOrNull(parsedUser.dealerId ?? parsedUser.id) : null,
          dealerName: role === 'dealer' ? parsedUser.dealerName || parsedUser.name || null : null,
          userId: this.toStringOrNull(parsedUser.userId ?? parsedUser.id),
          userName: parsedUser.name ?? null,
          siteCode: parsedUser.siteCode ?? null,
          nscId: role === 'nsc' ? parsedUser.nscId ?? null : null,
          nscName: role === 'nsc' ? parsedUser.nscName ?? null : null,
          language: parsedUser.language ?? null
        };

        console.log('3. [AuthService] loadUserFromStorage -> context restored', context);
   
        this.currentContextSubject.next(context);
        this.currentUserSubject.next(currentUser);
      } catch (e) {
        console.error('Failed to load user from storage', e);
      }
    }
  }

  /**
   * Get auth token
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }
  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Backend sends the role as a name (e.g. "Dealer"/"NSC") under role/roleName; roleId is numeric and not a reliable source.
  private resolveRole(
    user: { role?: string; roleName?: string; roleId?: string | number; roleID?: string | number; nscId?: string | null; dealerId?: string | number | null },
    fallback: 'dealer' | 'nsc' = 'dealer'
  ): 'dealer' | 'nsc' {
    const candidates = [user.role, user.roleName, user.roleId, user.roleID];

    for (const candidate of candidates) {
      if (typeof candidate !== 'string') {
        continue;
      }
      const raw = candidate.toLowerCase();
      if (raw.includes('nsc')) {
        return 'nsc';
      }
      if (raw.includes('dealer') || raw.includes('concession')) {
        return 'dealer';
      }
    }

    if (user.nscId && !user.dealerId) {
      return 'nsc';
    }

    return fallback;
  }

  private toStringOrNull(value: string | number | null | undefined): string | null {
    return value === null || value === undefined || value === '' ? null : String(value);
  }
}